/**
 * Turning an answer into blocks, so a comparison can be a table.
 *
 * Ask used to forbid structure outright — rule 6 of the system prompt said no
 * markdown, no headings, no bullets — and the surface rendered whatever came
 * back by splitting on blank lines into paragraphs. That was a defensible
 * choice for a product whose register is a document, and it failed the first
 * question a teacher actually brought: how many classes should I be teaching a
 * fortnight, when I have 51 at 50 minutes.
 *
 * The honest answer to that is a comparison — your minutes against the only
 * instrument in the corpus that caps them — and a comparison in flowing prose
 * makes a reader hold six numbers in their head and do the division themselves.
 * The same answer as four rows and three columns is read in a glance and can be
 * put in front of the employer.
 *
 * So a narrow grammar rather than a markdown library. Paragraphs and pipe
 * tables, nothing else: no raw HTML, no link syntax, no images, so there is no
 * sanitiser to get wrong and no dangerouslySetInnerHTML anywhere near a surface
 * that renders model output. Headings are deliberately absent too — an answer
 * that needs sections is an answer that should have been two questions.
 *
 * Parsed here rather than in the component because this app has no DOM test
 * harness and every other web test is over a pure function.
 */

export type AnswerBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'table'; header: string[]; rows: string[][] };

const isDivider = (line: string): boolean =>
  /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-');

const cells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

/**
 * A table needs a header, a divider and at least one row. Anything short of
 * that is prose that happens to contain a pipe — a clause quoting "either/or"
 * with a vertical bar in it should not silently become a one-column table.
 */
function tableAt(lines: string[], i: number): { block: AnswerBlock; next: number } | null {
  const header = lines[i];
  const divider = lines[i + 1];
  if (header === undefined || divider === undefined) return null;
  if (!header.includes('|') || !isDivider(divider)) return null;

  const head = cells(header);
  if (head.length < 2) return null;

  const rows: string[][] = [];
  let j = i + 2;
  while (j < lines.length) {
    const line = lines[j];
    if (line === undefined || line.trim() === '' || !line.includes('|')) break;
    const row = cells(line);
    // Ragged rows are padded rather than dropped. A model that omits a trailing
    // empty cell has still told the reader something, and losing the row loses
    // a number the answer was built on.
    while (row.length < head.length) row.push('');
    rows.push(row.slice(0, head.length));
    j += 1;
  }

  if (rows.length === 0) return null;
  return { block: { kind: 'table', header: head, rows }, next: j };
}

export function parseAnswer(text: string | null | undefined): AnswerBlock[] {
  if (text === null || text === undefined) return [];

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: AnswerBlock[] = [];
  let paragraph: string[] = [];

  const flush = (): void => {
    const joined = paragraph.join(' ').trim();
    if (joined !== '') blocks.push({ kind: 'paragraph', text: joined });
    paragraph = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;

    if (line.trim() === '') {
      flush();
      i += 1;
      continue;
    }

    const table = tableAt(lines, i);
    if (table !== null) {
      flush();
      blocks.push(table.block);
      i = table.next;
      continue;
    }

    paragraph.push(line.trim());
    i += 1;
  }

  flush();
  return blocks;
}
